/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/

import React, { useEffect } from 'react';
import cx from 'classnames';
import { get, isArray } from 'lodash';
import {AssetDropzoneProps} from "@broadleaf/admin-components/dist/common/components/AssetDropzone";
import useAssetDropzone from "@broadleaf/admin-components/dist/common/hooks/useAssetDropzone";
import {usePrevious} from "@broadleaf/admin-components/dist/common";
import {PDFUploadPreview} from "./PFUploadPreview";
import PDFAssetPreview from "./PDFAssetPreview";

/**
 * Component used to provide a dropzone for uploading files and assets.
 *
 * ```js
 * <AssetDropzone
 *   handleUpload={
 *     file => {
 *       // returns a Promise that resolves when the upload finishes
 *     }
 *   }
 *   style={{ width: 240, height: 240 }}
 * />
 * ```
 *
 * @see See `common/hooks/useAssetDropzone` for the hook that drives the internal state
 */
export const PDFAssetDropzone: React.FC<AssetDropzoneProps> = props => {
    const {
        clearUploads,
        isUploading,
        state,
        getInputProps,
        getRootProps
    } = useAssetDropzone(props);

    const wasUploading = usePrevious(isUploading);

    useEffect(() => {
        // clear uploads when we finish uploading
        if (wasUploading && !isUploading) {
            clearUploads();
        }
    });

    const renderUploadPreview = () => {
        if (!state.uploadIds || !state.uploadIds.length) return null;
        return state.uploadIds.map(uploadId => (
            <PDFUploadPreview
                {...state.uploadsById[uploadId]}
                className="AssetDropzone tw-mx-2"
                key={uploadId}
                previewStyle={props.previewStyle}
                previewClassName={cx(props.previewClassName, ' tw-w-full tw-h-full')}
                srcKey={props.srcKey}
                typeKey={props.typeKey}
            />
        ));
    };

    const renderAssetPreview = () => {
        if (props.value) return <AssetDropzoneValue {...props} />;

        return <AssetDropzoneEmpty message={props.emptyMessage} />;
    };

    return (
        <div
            {...getRootProps({
                className: cx(
                    'tw-relative tw-flex tw-items-center tw-justify-center tw-text-center tw-text-gray-400 tw-font-bold tw-bg-gray-100 tw-border-2 tw-border-dashed tw-rounded tw-outline-none AssetDropzone',
                    { 'tw-overflow-x-scroll': props.multiple },
                    props.className
                ),
                style: props.style
            })}
        >
            <input {...getInputProps()} />
            {isUploading ? renderUploadPreview() : renderAssetPreview()}
        </div>
    );
};


PDFAssetDropzone.defaultProps = {
    disabled: false,
    emptyMessage: 'Drop file here, or click to upload',
    multiple: false,
    noClick: false,
    srcKey: 'url',
    typeKey: 'type',
    value: null
};

const AssetDropzoneValue = props => {
    if (isArray(props.value)) {
        return props.value.map((value, index) => (
            <PDFAssetPreview
                className={cx('tw-h-full tw-w-full', props.previewClassName)}
                key={index}
                src={get(value, props.srcKey)}
                assetStyle={props.assetStyle}
                style={props.previewStyle}
                type={get(value, props.typeKey)}
            />
        ));
    }

    return (
        <PDFAssetPreview
            className={cx('tw-h-full tw-w-full', props.previewClassName)}
            src={get(props.value, props.srcKey)}
            assetStyle={props.assetStyle}
            style={props.previewStyle}
            type={get(props.value, props.typeKey)}
        />
    );
};

export const AssetDropzoneEmpty: React.FC<{ message: string }> = props => {
    return <div className="tw-w-full tw-text-sm">{props.message}</div>;
};

export default PDFAssetDropzone;